<!--
component: PlotIndex
author: Ken Nakamura
description: プロット地図索引図画面
-->
<template>
  <section class="plot-index">
    <!-- ローディング中の進捗バー -->
    <v-progress-linear
      color="deep-purple accent-4"
      rounded
      height="6"
      :active="loading"
      :indeterminate="loading"
      absolute
    ></v-progress-linear>
    <div class="split">
      <div class="split left-box">
        <ImageCanvas id="image-viewer"
          width="100%" height="100%"
          :imageUrl="imageUrl"
          :imageScale="imageScale"
          v-if="useCanvas" />
        <ImageViewer :imageUrl="imageUrl" :scaleSize="scaleSizeReset"
          @updateScaleSize="scaleSize = $event" width="100%"
          v-else />
      </div>
      <div class="split right-box">
        <v-container fluid>
          <v-row class="pl-1">
            <v-col cols="6" class="mx-0 px-0"
              v-for="(page, index) in pages" :key="index">
              <v-btn @click="showPage(index)" width="95%"
                  class="lighten-3 text-h7 blue"
                  :class="{yellow: pageNo==index}" style="margin-top:-8px;" >
                {{page.pageName}}
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="justify-center">
            <tenkey-pad @pushed="tenkeyPushed" @clear="clearNo"/>
          </v-row>
          <v-row>
            <v-col cols="5" class="justify-center">
              <label for="text-sheet-no"
                style="font-size: 1.4em;">
                図郭番号
              </label>
            </v-col>
            <v-col>
              <v-text-field id="text-sheet-no" v-model="sheetNo" ref="sheetNo"
                solo dense type="tel" autocomplete="off" hide-details="auto"
                @keyup="checkChar"
                class="text-h6"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-btn color="#4063a3" height="1.6em"
              class="search-btn"
              :disabled="sheetNo==''"
              @click="gotoPlotMap()"
            >プロット図閲覧
            </v-btn>
          </v-row>
          <v-row class="my-1" dense>
            <v-col cols=6 class="px-1 py-0">
              <v-btn color="#5dabbe"
                class="page-btn my-1"
                :disabled="pageNo<1"
                @click="prevPage">
                <v-icon color="#dddddd" class="mx-1">mdi-step-backward</v-icon>
                前ページ
              </v-btn>
            </v-col>
            <v-col cols=6 class="px-1 py-0">
              <v-btn color="#5dabbe"
                class="page-btn my-1"
                :disabled="pageNo>=pages.length-1"
                @click="nextPage">
                <v-icon color="#dddddd" class="mx-1">mdi-step-forward</v-icon>
                次ページ
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="my-1" dense>
            <v-col cols=4 class="px-0 py-0">
              <v-btn color="#5dabbe"
                class="page-btn mx-0 my-1"
                @click="zoomIn">
                <v-icon color="#dddddd" class="ma-auto">mdi-plus-circle</v-icon>
                拡大
              </v-btn>
            </v-col>
            <v-col cols=4 class="px-0 py-0">
              <v-btn color="#5dabbe"
                class="page-btn mx-0 my-1"
                @click="zoomOut">
                <v-icon color="#dddddd" class="ma-auto">mdi-minus-circle</v-icon>
                縮小
              </v-btn>
            </v-col>
            <v-col cols=4 class="px-0 py-0">
              <v-btn color="#5dabbe" width="100%"
                class="page-btn mx-0 my-1"
                @click="zoomEntire">
                <v-icon small color="#dddddd" class="ma-auto">mdi-arrow-expand-all</v-icon>
                全体表示
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>
  </section>
</template>

<script>
import ImageViewer from '../components/ImageViewer.vue';
import ImageCanvas from '../components/ImageCanvas.vue';
import TenkeyPad from '../components/TenkeyPad.vue'
import { dbglog } from '../common/utils';
import { loadKenkakuPlotIndex, loadKaihatsuPlotIndex } from '../common/apiAccess.js'

export default {
  components: {
    ImageViewer,
    ImageCanvas,
    TenkeyPad,
  },
  data: () => {
    return {
      pages: [],
      pageNo: -1,
      sheetNo: '',
      scaleSize: 1,
      loading: false,
      useCanvas: true,
      imageScale: 1,  // ImageCanvasに渡す画像拡大率（画面横幅いっぱいを1とする）
    }
  },
  props: {
    id: {
      type: Number,
      default: 0,
    }
  },
  watch: {
    sheetNo: {
      immediate: true,
      handler(val) {
        if (val) {
          val = parseInt(val);
          if (Number.isNaN(val) || val <= 0) {
            val = '';
          }
          this.$refs.sheetNo.lazyValue = val;
          this.sheetNo = val;
        }
      }
    },
  },
  mounted () {
    try {
      if (this.unauthorized) {
        this.message = 'ユーザー認証されていません。';
        this.$router.replace('/');
        return;
      }

      this.loading = true;
      if (this.id > 0) {
        this.indexId = this.id;
      }

      if (this.mode == 1) {
        this.message = '建築確認申請プロット地図検索 : ' + this.wardName;
        loadKenkakuPlotIndex(this.indexId, (data) => {
          this.nendoName = data.nendoName;
          this.pages = data.pages;
          this.loading = false;
          this.message = `建築確認申請プロット地図【${this.wardName} ${this.nendoName}】索引図`;
        })
      }
      else if (this.mode == 5 || this.mode == 6) {
        this.message = '開発・造成・土石の堆積プロット地図検索 : ' + this.wardName;
        loadKaihatsuPlotIndex(this.indexId, (data) => {
          this.nendoName = data.nendoName;
          this.pages = data.pages;
          this.loading = false;
          this.message = `開発・造成・土石の堆積プロット地図【${this.wardName} ${this.nendoName}】索引図`;
          dbglog(`PlotIndex.mounted: page count = ${this.pages.length}`);
          dbglog(this.pages[0])
        })
      }
      this.pageNo = 0;
    }
    catch (err) {
      console.log(err);
    }
    finally {
      //プロット地図画面の入力値をクリア
      this.$store.commit('setSearchYear', 0);
      this.$store.commit('setSearchBango', '');
    }
  },
  computed: {
    token () {
      return this.$store.state.token;
    },
    unauthorized () {
      return this.token == undefined || this.token == null || this.token.length == 0;
    },
    authorized () {
      return !this.unauthorized;
    },
    mode () {
      return this.$store.state.mode;
    },
    wardName () {
      return this.$store.state.wardName;
    },
    nendoName: {
      get() { return this.$store.state.nendoName; },
      set(val) { this.$store.commit('setNendoName', val); }
    },
    indexId: {
      get() { return this.$store.state.indexId; },
      set(val) { this.$store.commit('setIndexId', val); }
    },
    imageUrl () {
      if (this.pages === undefined || this.pages === null || this.pages.length === undefined) {
        return '';
      }
      if (this.pageNo < 0 || this.pageNo >= this.pages.length) {
        return '';
      }
      const page = this.pages[this.pageNo];
      dbglog(page);
      let origin = '';
      if (this.mode == 1) {
        origin = this.$store.state.kenkakuPlotIndexOrigin;
      }
      else if (this.mode == 5 || this.mode == 6) {
        origin = this.$store.state.kaihatsuPlotIndexOrigin;
      }
      const url = origin + '/' + page.fileName;
      dbglog(url);
      return url;
    },
    scaleSizeReset() {
      return this.scaleSize;
    },
    message: {
      get() { return this.$store.state.message; },
      set(val) { this.$store.commit('setMessage', val) }
    },
  },
  methods: {
    tenkeyPushed(n) {
      this.sheetNo = this.sheetNo * 10 + n;
    },
    clearNo() {
      this.sheetNo = '';
    },
    showPage (no) {
      if (no >= 0 && no < this.pages.length) {
        this.pageNo = no;
        this.scaleSize = 1;
      }
    },
    prevPage() {
      this.showPage(this.pageNo - 1);
    },
    nextPage() {
      this.showPage(this.pageNo + 1);
    },
    zoomIn () {
      if(this.scaleSize < 10){
        this.scaleSize += 1;
      }
      this.imageScale *= 1.5;
    },
    zoomOut () {
      if(this.scaleSize > 1){
        this.scaleSize -= 1;
      }
      if (this.imageScale > 1) {
        this.imageScale = Math.max(1, this.imageScale / 1.5);
      }
    },
    zoomEntire() {
      if (this.useCanvas) {
        this.imageScale = 1;
      }
      else {
        this.scaleSize = 1;
      }
    },
    checkChar () {
      this.sheetNo = String(this.sheetNo).replace(/[^0-9]/g, '');
    },
    gotoPlotMap () {
      this.$router.push({ name: 'plot-map', params: { sheet: this.sheetNo } });
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/common/common.css";
@import "../styles/split-alt.css";
.split {
  width: 100%;
  height: calc(100% - 13px);
  padding: 6px 0;
  margin: 0;
}
.left-box {
  left: 0;
  width: calc(100% - 300px);
  //height: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
}
.right-box {
  right: 0;
  //width: 30%;
  width: 300px;
  background-color: rgb(162, 235, 245);
  //padding-bottom: 20px;
  overflow: auto;
}

.search-btn {
  border-radius: 5px;
  border: 0px;
  background-color: #4063a3;
  margin: 10px;
  padding: 10em;
  font-weight: 400;
  text-align: center;
  text-decoration: none;
  color: #cbe7e5;
  font-size: 1.4em;
  user-select: none;
  width: 90%;
  height: 1.5em;
}

.index-btn {
  margin: 0 !important;
}

.key-pad {
  background-color: #afdfdf;
  width: 250px;
  height: 270px;
  padding: 10px 10px;
  font-size: 12pt;
}

</style>